import { getFirestore, collection, query, where, getDocs, getDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';

/**
 * Validates and uses an invite URL code
 * @param {string} inviteCode - The invite code from the URL
 * @returns {Promise<{valid: boolean, urlId: string|null, error: string|null}>}
 */
export const validateInviteUrl = async (inviteCode) => {
  try {
    if (!inviteCode || !inviteCode.trim()) {
      return { valid: false, urlId: null, error: 'Invite code is required' };
    }

    const db = getFirestore();
    const inviteUrlsRef = collection(db, 'inviteUrls');
    const q = query(
      inviteUrlsRef,
      where('inviteCode', '==', inviteCode.toUpperCase()),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { valid: false, urlId: null, error: 'Invalid or inactive invite code' };
    }

    const inviteUrlDoc = querySnapshot.docs[0];
    const inviteUrlData = inviteUrlDoc.data();
    const urlId = inviteUrlDoc.id;

    // Check if URL is expired
    if (inviteUrlData.expirationType === 'date' && inviteUrlData.expirationDate) {
      const expirationDate = inviteUrlData.expirationDate.toDate ? 
        inviteUrlData.expirationDate.toDate() : 
        new Date(inviteUrlData.expirationDate);
      
      if (new Date() > expirationDate) {
        return { valid: false, urlId: null, error: 'This invite link has expired' };
      }
    }

    // Check if URL has reached user limit
    if (inviteUrlData.expirationType === 'users' && inviteUrlData.maxUsers) {
      const usedCount = inviteUrlData.usedCount || 0;
      if (usedCount >= inviteUrlData.maxUsers) {
        return { valid: false, urlId: null, error: 'This invite link has reached its user limit' };
      }
    }

    return { valid: true, urlId, error: null };
  } catch (error) {
    console.error('Error validating invite URL:', error);
    return { valid: false, urlId: null, error: 'Error validating invite code' };
  }
};

/**
 * Records that an invite URL was used by a user
 * @param {string} urlId - The invite URL document ID
 * @param {string} userId - The user ID who used the invite
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export const useInviteUrl = async (urlId, userId) => {
  try {
    const db = getFirestore();
    const urlRef = doc(db, 'inviteUrls', urlId);
    const urlDoc = await getDoc(urlRef);
    
    if (!urlDoc.exists()) {
      return { success: false, error: 'Invite URL not found' };
    }
    
    // Get current data
    const currentData = urlDoc.data();

    // Check if user already used this URL
    const usedBy = currentData.usedBy || [];
    if (usedBy.includes(userId)) {
      return { success: false, error: 'You have already used this invite link' };
    }

    // Update the invite URL
    await updateDoc(urlRef, {
      usedCount: (currentData.usedCount || 0) + 1,
      usedBy: [...usedBy, userId],
      lastUsedAt: serverTimestamp()
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error using invite URL:', error);
    return { success: false, error: 'Error recording invite URL usage' };
  }
};

/**
 * Gets invite code from URL parameters (web) or route params (mobile)
 * @param {Object} routeParams - Route params from navigation
 * @returns {string|null} - The invite code or null
 */
export const getInviteCodeFromUrl = (routeParams = {}) => {
  // Check route params first (for mobile/navigation)
  if (routeParams?.invite) {
    return routeParams.invite;
  }

  // Check URL search params (for web)
  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('invite');
  }

  return null;
};

